import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { createCheckout } from "../lib/api";
import {
  Heart, Repeat, GraduationCap, Sparkles, Loader2, Building2, Package, Banknote, Star, Users, School,
} from "lucide-react";
import { SIGNATURE_PROGRAMS } from "../lib/signaturePrograms";

/**
 * Giving options. `recurring` governs whether Stripe handles it as subscription.
 * `fixed_amount` lets us lock the price so admin tiers aren't editable from client.
 */
const WAYS = [
  {
    id: "sponsor_young_man",
    icon: GraduationCap,
    title: "Sponsor a Young Man",
    tagline: "Directly support one boy's year.",
    recurring: true,
    lookup_key: "sponsor_student",
    tiers: [
      { amount: 35, label: "$35/mo", note: "Covers mentoring supplies for one boy" },
      { amount: 75, label: "$75/mo", note: "Programs + materials for one boy" },
      { amount: 150, label: "$150/mo", note: "Fully sponsors one boy's year", featured: true },
    ],
    accent: "#6B21A8",
  },
  {
    id: "sponsor_program",
    icon: Star,
    title: "Sponsor a Program",
    tagline: "Underwrite real programming for the whole cohort.",
    recurring: false,
    lookup_key: "sponsor_program",
    allowProgramSelect: true,
    tiers: [
      { amount: 500, label: "$500", note: "Fund one weekly session (any program)" },
      { amount: 2500, label: "$2,500", note: "Underwrite a 12-week Turnaround Project cohort" },
      { amount: 10000, label: "$10,000", note: "Sponsor a 24-week Strong Minds cohort", featured: true },
    ],
    accent: "#F5B90A",
  },
  {
    id: "corporate",
    icon: Building2,
    title: "Corporate Sponsorship",
    tagline: "Partner at the organizational level — recognition on our site.",
    recurring: false,
    lookup_key: "corporate",
    tiers: [
      { amount: 5000, label: "$5,000", note: "Community Partner" },
      { amount: 15000, label: "$15,000", note: "Program Partner — logo on relevant program page", featured: true },
      { amount: 50000, label: "$50,000", note: "Signature Partner — logo on homepage + Strong Minds" },
    ],
    accent: "#111111",
  },
  {
    id: "supplies",
    icon: Package,
    title: "Supplies & Equipment",
    tagline: "Books, uniforms, snacks, laptops — the real stuff.",
    recurring: false,
    lookup_key: "supplies",
    tiers: [
      { amount: 50, label: "$50", note: "Workbooks for one cohort" },
      { amount: 150, label: "$150", note: "Session snacks & meals for a month" },
      { amount: 500, label: "$500", note: "Technology for an entire group" },
    ],
    accent: "#6B21A8",
  },
  {
    id: "operating",
    icon: Banknote,
    title: "General Operating Support",
    tagline: "Unrestricted funds help where they're needed most.",
    recurring: false,
    lookup_key: "operating",
    tiers: [
      { amount: 100, label: "$100", note: "" },
      { amount: 500, label: "$500", note: "" },
      { amount: 2500, label: "$2,500", note: "" },
    ],
    accent: "#D4AF37",
  },
];

function GiveCard({ way }) {
  const [tierIdx, setTierIdx] = useState(way.tiers.findIndex((t) => t.featured) === -1 ? 0 : way.tiers.findIndex((t) => t.featured));
  const [custom, setCustom] = useState("");
  const [donor, setDonor] = useState({ name: "", email: "" });
  const [program, setProgram] = useState("change-choices");
  const [loading, setLoading] = useState(false);

  const amount = custom ? Number(custom) : way.tiers[tierIdx].amount;

  const submit = async () => {
    if (!amount || amount < 1) { toast.error("Please choose or enter an amount."); return; }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: way.lookup_key,
        amount: Number(amount),
        origin_url: window.location.origin,
        donor_name: donor.name || undefined,
        donor_email: donor.email || undefined,
        recurring: !!way.recurring,
        designation: way.allowProgramSelect ? program : undefined,
      });
      window.location.href = checkout_url;
    } catch (e) {
      toast.error("We couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <div className="rounded-xl bg-white border border-black/10 p-6 md:p-7 flex flex-col gap-5" data-testid={`give-card-${way.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-md" style={{ backgroundColor: `${way.accent}15`, color: way.accent }}>
          <way.icon className="h-5 w-5" strokeWidth={2} />
        </div>
        <div>
          <h3 className="font-display text-xl font-bold">{way.title}</h3>
          <p className="text-sm text-black/60">{way.tagline}</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2" data-testid={`tiers-${way.id}`}>
        {way.tiers.map((t, i) => (
          <button
            key={i}
            onClick={() => { setTierIdx(i); setCustom(""); }}
            data-testid={`tier-${way.id}-${t.amount}`}
            className={`relative rounded-md border p-3 text-left transition ${!custom && tierIdx === i ? "border-[#6B21A8] bg-[#6B21A8]/5" : "border-black/10 hover:border-[#6B21A8]"}`}
          >
            {t.featured && <span className="absolute -top-2 left-2 bg-[#F5B90A] text-black text-[10px] font-bold px-2 py-0.5 rounded-full uppercase">Most popular</span>}
            <p className="font-display text-lg font-bold">{t.label}</p>
            {t.note && <p className="mt-1 text-[11px] text-black/60 leading-tight">{t.note}</p>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 rounded-md border border-black/10 bg-white px-3 py-2">
        <span className="text-black/50 text-sm">$</span>
        <input
          type="number"
          min="1"
          value={custom}
          onChange={(e) => setCustom(e.target.value)}
          placeholder="Or enter a custom amount"
          data-testid={`custom-${way.id}`}
          className="w-full text-sm outline-none"
        />
      </div>

      {way.allowProgramSelect && (
        <div className="grid gap-1">
          <label className="overline text-black/50 text-[11px]">Designate to a program (optional)</label>
          <select value={program} onChange={(e) => setProgram(e.target.value)} data-testid={`program-select-${way.id}`} className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm">
            {SIGNATURE_PROGRAMS.map((p) => <option key={p.slug} value={p.slug}>{p.title}</option>)}
            <option value="general">Where it's needed most</option>
          </select>
        </div>
      )}

      <div className="grid gap-2 md:grid-cols-2">
        <input placeholder="Your name (optional)" value={donor.name} onChange={(e) => setDonor({ ...donor, name: e.target.value })} data-testid={`donor-name-${way.id}`} className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm" />
        <input type="email" placeholder="Email for receipt" value={donor.email} onChange={(e) => setDonor({ ...donor, email: e.target.value })} data-testid={`donor-email-${way.id}`} className="rounded-md border border-black/10 bg-white px-3 py-2 text-sm" />
      </div>

      <button onClick={submit} disabled={loading} className="btn-primary w-full" data-testid={`give-submit-${way.id}`}>
        {loading ? <><Loader2 className="h-5 w-5 animate-spin" /> Redirecting…</> : <><Sparkles className="h-5 w-5" /> Give ${amount || 0}{way.recurring ? "/mo" : ""}</>}
      </button>
    </div>
  );
}

export default function Donate() {
  return (
    <>
      {/* HERO */}
      <section className="relative overflow-hidden bg-[#0A0A0A] text-white" data-testid="donate-hero">
        <div className="noise" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#6B21A8]/35 via-transparent to-transparent" />
        <div className="container-x relative py-20 md:py-28 grid gap-12 md:grid-cols-12 items-center">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 mb-5">
              <span className="gold-line" />
              <span className="overline text-[#F5B90A]">Donate</span>
            </div>
            <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02]">
              INVEST IN A BOY.<br />
              <span className="italic text-[#F5B90A]">Help build a man.</span>
            </h1>
            <p className="mt-7 max-w-xl text-white/85 text-lg">
              Every dollar funds mentorship, prevention, meals, materials, and the moments that change a young man's trajectory.
              Choose how you want to give.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href="#sponsor_young_man" className="btn-gold" data-testid="hero-scroll-sponsor">Sponsor a Young Man</a>
              <Link to="/kings-circle" className="btn-outline-white" data-testid="hero-kings-circle">Join Kings' Circle</Link>
            </div>
          </div>
          <div className="md:col-span-5 flex justify-center md:justify-end relative">
            <div className="absolute inset-6 bg-[#F5B90A]/20 rounded-full blur-3xl" />
            <img src="/coach-kaf-laugh.png" alt="Coach Kaf" className="relative h-[320px] md:h-[420px] w-auto drop-shadow-[0_20px_40px_rgba(245,185,10,0.35)]" />
          </div>
        </div>
      </section>

      {/* QUICK GIVE (one-time, legacy) */}
      <section className="bg-[#F7F3EC] py-16" data-testid="quick-give">
        <div className="container-x max-w-5xl">
          <div className="rounded-xl bg-white border border-black/10 p-7 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-start gap-4">
              <div className="flex h-11 w-11 items-center justify-center rounded-md bg-[#F5B90A]/20 text-[#F5B90A]">
                <Heart className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-display text-xl font-bold">Just want to give a one-time gift?</h3>
                <p className="text-sm text-black/60">Pick any amount and your donation goes where it's needed most.</p>
              </div>
            </div>
            <QuickGive />
          </div>
        </div>
      </section>

      {/* SPONSOR TIERS */}
      <section id="sponsor_young_man" className="bg-white py-20 md:py-24" data-testid="sponsor-tiers">
        <div className="container-x">
          <div className="max-w-3xl mb-14">
            <span className="overline text-[#6B21A8]">Ways To Give</span>
            <h2 className="font-display text-4xl md:text-5xl font-bold mt-2">Choose how you want to invest.</h2>
            <p className="mt-4 text-black/70 text-lg">
              From monthly sponsorship of one young man to signature partnership of our flagship program, every tier makes real
              change possible.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {WAYS.map((w) => <GiveCard key={w.id} way={w} />)}
          </div>
        </div>
      </section>

      {/* KINGS' CIRCLE CTA */}
      <section className="bg-[#0A0A0A] text-white py-16" data-testid="donate-kings-cta">
        <div className="container-x mx-auto max-w-3xl text-center">
          <span className="overline text-[#F5B90A]">Monthly Donor Community</span>
          <h2 className="mt-3 font-display text-4xl md:text-5xl font-bold">Join the Kings' Circle.</h2>
          <p className="mt-4 text-white/70 text-lg">
            Monthly donors get named on our public wall and receive quarterly impact updates. Our brotherhood of supporters.
          </p>
          <Link to="/kings-circle" className="mt-8 btn-gold inline-flex" data-testid="donate-join-kings"><Users className="h-4 w-4" /> Join Kings' Circle</Link>
        </div>
      </section>

      {/* RECEIPT NOTE */}
      <section className="bg-white py-10">
        <div className="container-x mx-auto max-w-3xl text-center">
          <p className="text-xs text-black/50">
            Secure checkout powered by Stripe. The Change Center Mentoring Group is a 501(c)(3) nonprofit — your gift may be tax-deductible in the U.S. EIN on request.
          </p>
        </div>
      </section>
    </>
  );
}

function QuickGive() {
  const [amount, setAmount] = useState(100);
  const [custom, setCustom] = useState("");
  const [loading, setLoading] = useState(false);
  const final = custom ? Number(custom) : amount;

  const go = async () => {
    if (!final || final < 1) { toast.error("Pick an amount of at least $1."); return; }
    setLoading(true);
    try {
      const { checkout_url } = await createCheckout({
        lookup_key: "one_time", amount: Number(final),
        origin_url: window.location.origin, recurring: false,
      });
      window.location.href = checkout_url;
    } catch { toast.error("We couldn't start checkout."); setLoading(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="quick-give-row">
      {[25, 50, 100, 250, 500].map((a) => (
        <button key={a} onClick={() => { setAmount(a); setCustom(""); }} data-testid={`quick-amt-${a}`}
          className={`rounded-md border px-3 py-2 text-sm font-semibold transition ${!custom && amount === a ? "border-[#6B21A8] bg-[#6B21A8] text-white" : "border-black/10 hover:border-[#6B21A8]"}`}>
          ${a}
        </button>
      ))}
      <input type="number" min="1" value={custom} onChange={(e) => setCustom(e.target.value)} placeholder="Custom" className="rounded-md border border-black/10 px-3 py-2 text-sm w-28" data-testid="quick-custom" />
      <button onClick={go} disabled={loading} className="btn-gold" data-testid="quick-give-btn">
        {loading ? "…" : `Give $${final || 0}`}
      </button>
    </div>
  );
}
